import { supabase } from "@/integrations/supabase/client";
import { notifyClient } from "@/lib/notify";
import { dispatchLinkFor, shipperLoadLink } from "@/lib/lrDirect/notifyLinks";

export type DispatchStatus =
  | "draft" | "ready"
  | "assigned"
  | "en_route_to_pickup" | "at_pickup" | "picked_up"
  | "en_route_to_delivery" | "at_delivery" | "delivered"
  | "closed";

export const STATUS_LABEL: Record<DispatchStatus, string> = {
  draft: "Draft",
  ready: "Ready",
  assigned: "Assigned",
  en_route_to_pickup: "En route to pickup",
  at_pickup: "At pickup",
  picked_up: "Picked up",
  en_route_to_delivery: "En route to delivery",
  at_delivery: "At delivery",
  delivered: "Delivered",
  closed: "Closed",
};

export const STATUS_TONE: Record<DispatchStatus, string> = {
  draft: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  ready: "bg-sky-500/10 text-sky-700 border-sky-500/30",
  assigned: "bg-brand-teal/15 text-brand-teal border-brand-teal/30",
  en_route_to_pickup: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  at_pickup: "bg-indigo-500/10 text-indigo-700 border-indigo-500/30",
  picked_up: "bg-violet-500/10 text-violet-700 border-violet-500/30",
  en_route_to_delivery: "bg-purple-500/10 text-purple-700 border-purple-500/30",
  at_delivery: "bg-fuchsia-500/10 text-fuchsia-700 border-fuchsia-500/30",
  delivered: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  closed: "bg-muted text-muted-foreground border-border",
};

// Broker-driven progression (in order).
export const BROKER_FLOW: DispatchStatus[] = [
  "assigned",
  "en_route_to_pickup",
  "at_pickup",
  "picked_up",
  "en_route_to_delivery",
  "at_delivery",
  "delivered",
];

export function nextBrokerStatuses(current: DispatchStatus): DispatchStatus[] {
  const idx = BROKER_FLOW.indexOf(current);
  if (idx < 0) return BROKER_FLOW.slice(1); // unknown → all forward steps
  return BROKER_FLOW.slice(idx + 1);
}

export interface UpdateStatusArgs {
  dispatchId: string;
  loadId: string;
  newStatus: DispatchStatus;
  actorId: string;
  notes?: string;
  counterpartyUserId: string | null;
  /** Load's shipper (direct_loads.created_by). Used for milestone notifications. */
  shipperUserId?: string | null;
  /** Optional load reference for friendlier copy. */
  loadNumber?: string | null;
  /** When true, this status was auto-filled by a forward-jump backfill (not an explicit broker click on this stage). */
  backfilled?: boolean;
}

const SHIPPER_MILESTONES: Partial<Record<DispatchStatus, (ref: string) => { title: string; message: string }>> = {
  assigned: (ref) => ({ title: "Carrier assigned", message: `Carrier assigned for ${ref}` }),
  picked_up: (ref) => ({ title: "Picked up", message: `Driver picked up ${ref}` }),
  delivered: (ref) => ({ title: "Delivered", message: `Load delivered: ${ref}` }),
};

export async function updateDispatchStatus({
  dispatchId, loadId, newStatus, actorId, notes, counterpartyUserId,
  shipperUserId, loadNumber, backfilled,
}: UpdateStatusArgs) {
  const nowIso = new Date().toISOString();
  const patch: Record<string, any> = {
    status: newStatus,
    updated_at: nowIso,
  };

  // Marking Ready is the strongest signal that the shipper has finished the
  // dispatch packet. Stamp it here too so alternate ready paths cannot leave
  // the broker stuck in the preparation state.
  if (newStatus === "ready" && shipperUserId && actorId === shipperUserId) {
    patch.dispatch_prepared_at = nowIso;
    patch.dispatch_prepared_by = actorId;
  }

  const { data: updatedRows, error: updErr } = await supabase
    .from("direct_dispatches" as any)
    .update(patch)
    .eq("id", dispatchId)
    .select("id, dispatch_prepared_at");
  if (updErr) throw updErr;
  if (!updatedRows || updatedRows.length === 0) {
    throw new Error("Status update failed: no dispatch row was updated. You may not have permission.");
  }
  if (newStatus === "ready" && shipperUserId && actorId === shipperUserId && !(updatedRows[0] as any).dispatch_prepared_at) {
    throw new Error("Dispatch ready update failed: the prepared timestamp was not saved.");
  }

  const { data: eventRows, error: eventErr } = await supabase.from("direct_dispatch_events" as any).insert({
    dispatch_id: dispatchId,
    actor_id: actorId,
    event_type: `status_${newStatus}`,
    payload: { status: newStatus, notes: notes ?? null, backfilled: backfilled ?? false },
  }).select("id");
  if (eventErr) throw eventErr;
  if (!eventRows || eventRows.length === 0) {
    throw new Error("Status update failed: no timeline event was created.");
  }

  // Fetch lane for notification copy (best-effort; degrade to "" if not found).
  let lane = "";
  try {
    const { data: dl } = await supabase
      .from("direct_loads" as any)
      .select("origin_city, origin_state, destination_city, destination_state")
      .eq("id", loadId)
      .maybeSingle();
    if (dl) {
      const d: any = dl;
      lane = `${d.origin_city}, ${d.origin_state} → ${d.destination_city}, ${d.destination_state}`;
    }
  } catch { /* noop */ }

  if (counterpartyUserId) {
    // Use specific dispatch_ready event when transitioning to "ready"; otherwise generic status_changed.
    const isReady = newStatus === "ready";
    const statusLbl = STATUS_LABEL[newStatus];
    notifyClient({
      recipientUserId: counterpartyUserId,
      eventType: isReady ? "dispatch_ready" : "dispatch_status_changed",
      inAppType: "dispatch_status_changed",
      title: `📦 Load Update`,
      body: [statusLbl, lane].filter(Boolean).join(" · "),
      link: dispatchLinkFor(counterpartyUserId, shipperUserId, loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, status: newStatus },
      smsBody: isReady
        ? `LR Direct: Dispatch ready${loadNumber ? ` for ${loadNumber}` : ""}. Open the load to begin.`
        : undefined,
    });
  }

  // Shipper milestone notification (assigned / picked_up / delivered)
  const milestone = SHIPPER_MILESTONES[newStatus];
  if (
    milestone &&
    shipperUserId &&
    shipperUserId !== actorId &&
    shipperUserId !== counterpartyUserId
  ) {
    const sinceIso = new Date(Date.now() - 60_000).toISOString();
    const { data: recent } = await supabase
      .from("notifications")
      .select("id, metadata")
      .eq("recipient_user_id", shipperUserId)
      .eq("type", "load_milestone")
      .eq("load_id", loadId)
      .gte("created_at", sinceIso)
      .limit(20);
    const duplicate = (recent ?? []).some((n: any) => n?.metadata?.status === newStatus);
    if (!duplicate) {
      const statusLbl = STATUS_LABEL[newStatus];
      const eventType =
        newStatus === "picked_up" ? "load_milestone_picked_up" :
        newStatus === "delivered" ? "load_milestone_delivered" :
        "load_milestone_assigned";
      notifyClient({
        recipientUserId: shipperUserId,
        eventType,
        inAppType: "load_milestone",
        title: `📦 Load Update`,
        body: [statusLbl, lane].filter(Boolean).join(" · "),
        link: shipperLoadLink(loadId),
        loadId,
        metadata: { dispatch_id: dispatchId, status: newStatus },
      });
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Broker-clickable tracker stages
// ─────────────────────────────────────────────────────────────────────────────

import type { StageKey } from "@/lib/lrDirect/deriveStages";

/** Ordered list of broker-clickable stages on the tracker. */
export const BROKER_STAGE_ORDER: StageKey[] = [
  "truck_assigned",
  "picked_up",
  "in_transit",
  "delivered",
  "closed",
];

/** Which DispatchStatus each broker-clickable stage writes. */
export const STAGE_TO_STATUS: Record<StageKey, DispatchStatus | null> = {
  posted: null,
  awarded: null,
  dispatch_created: null,
  broker_accepted: null,
  truck_assigned: "assigned",
  pickup_scheduled: null,
  picked_up: "picked_up",
  in_transit: "en_route_to_delivery",
  delivered: "delivered",
  pod_uploaded: null, // auto on POD upload
  closed: "closed",
};

/** Inverse: DispatchStatus → StageKey (for the broker-driven ones only). */
const STATUS_TO_STAGE: Partial<Record<DispatchStatus, StageKey>> = {
  assigned: "truck_assigned",
  picked_up: "picked_up",
  en_route_to_delivery: "in_transit",
  delivered: "delivered",
  closed: "closed",
};

/** All status_* event types that map to a broker-driven stage. */
const BROKER_STATUS_EVENT_TYPES: string[] = [
  "status_assigned",
  "status_en_route_to_pickup",
  "status_at_pickup",
  "status_picked_up",
  "status_en_route_to_delivery",
  "status_at_delivery",
  "status_delivered",
  "status_closed",
];

/** Event types cleared when undoing a given broker stage (this stage + all later). */
function eventTypesToClearForUndo(stageKey: StageKey): string[] {
  switch (stageKey) {
    case "truck_assigned":
      return BROKER_STATUS_EVENT_TYPES; // clear everything from assigned onward
    case "picked_up":
      return [
        "status_en_route_to_pickup", "status_at_pickup", "status_picked_up",
        "status_en_route_to_delivery", "status_at_delivery",
        "status_delivered", "status_closed",
      ];
    case "in_transit":
      return [
        "status_en_route_to_delivery", "status_at_delivery",
        "status_delivered", "status_closed",
      ];
    case "delivered":
      return ["status_at_delivery", "status_delivered", "status_closed"];
    case "closed":
      return ["status_closed"];
    default:
      return [];
  }
}

export interface AdvanceToStageArgs {
  dispatchId: string;
  loadId: string;
  targetStageKey: StageKey;
  currentStageKey: StageKey | null;
  actorId: string;
  counterpartyUserId: string | null;
  shipperUserId?: string | null;
  loadNumber?: string | null;
}

/**
 * Advance the dispatch to `targetStageKey`, backfilling any skipped broker stages
 * in order with `backfilled: true`. Fires notifications for each intermediate
 * status write (existing updateDispatchStatus behavior).
 */
export async function advanceToStage({
  dispatchId, loadId, targetStageKey, currentStageKey, actorId,
  counterpartyUserId, shipperUserId, loadNumber,
}: AdvanceToStageArgs): Promise<void> {
  const targetIdx = BROKER_STAGE_ORDER.indexOf(targetStageKey);
  if (targetIdx < 0) throw new Error("Stage is not broker-clickable");

  const currentIdx = currentStageKey ? BROKER_STAGE_ORDER.indexOf(currentStageKey) : -1;
  const startIdx = currentIdx + 1;
  if (startIdx > targetIdx) return; // already past target

  for (let i = startIdx; i <= targetIdx; i++) {
    const stage = BROKER_STAGE_ORDER[i];
    const status = STAGE_TO_STATUS[stage];
    if (!status) continue;
    const isFinal = i === targetIdx;
    await updateDispatchStatus({
      dispatchId, loadId, newStatus: status, actorId,
      counterpartyUserId, shipperUserId, loadNumber,
      backfilled: !isFinal,
    });
  }
}

export interface UndoStageArgs {
  dispatchId: string;
  loadId: string;
  stageKey: StageKey;
  actorId: string;
  counterpartyUserId: string | null;
}

/**
 * Clear the given stage AND every later broker stage from the timeline, then
 * recompute direct_dispatches.status from the newest remaining broker
 * status_* event. Writes a status_undone audit event and notifies the
 * counterparty of the change.
 */
export async function undoDispatchStage({
  dispatchId, loadId, stageKey, actorId, counterpartyUserId,
}: UndoStageArgs): Promise<DispatchStatus> {
  const clearTypes = eventTypesToClearForUndo(stageKey);
  if (clearTypes.length === 0) throw new Error("Stage cannot be undone");

  // 1. Delete the status_* events for this stage and all later broker stages.
  //    .select() forces PostgREST to return the affected rows so we can
  //    detect a silent RLS failure (0 rows deleted despite no error).
  const { data: deletedRows, error: delErr } = await supabase
    .from("direct_dispatch_events" as any)
    .delete()
    .eq("dispatch_id", dispatchId)
    .in("event_type", clearTypes)
    .select("id");
  if (delErr) throw delErr;
  if (!deletedRows || deletedRows.length === 0) {
    throw new Error(
      "Undo failed: no timeline events were removed. You may not have permission to undo this stage."
    );
  }

  // 2. Recompute status from newest remaining broker status_* event.
  const { data: remaining } = await supabase
    .from("direct_dispatch_events" as any)
    .select("event_type, created_at")
    .eq("dispatch_id", dispatchId)
    .in("event_type", BROKER_STATUS_EVENT_TYPES)
    .order("created_at", { ascending: false })
    .limit(1);
  const latestType = (remaining as any[] | null)?.[0]?.event_type as string | undefined;
  const recomputed: DispatchStatus =
    (latestType?.replace(/^status_/, "") as DispatchStatus | undefined) ?? "draft";

  // 3. Write the new status back.
  const { data: updRows, error: updErr } = await supabase
    .from("direct_dispatches" as any)
    .update({ status: recomputed, updated_at: new Date().toISOString() })
    .eq("id", dispatchId)
    .select("id");
  if (updErr) throw updErr;
  if (!updRows || updRows.length === 0) {
    throw new Error("Undo failed: dispatch status was not updated. You may not have permission.");
  }

  // 4. Audit event.
  await supabase.from("direct_dispatch_events" as any).insert({
    dispatch_id: dispatchId,
    actor_id: actorId,
    event_type: "status_undone",
    payload: {
      undone_stage: stageKey,
      cleared_event_types: clearTypes,
      reverted_to: recomputed,
    },
  });

  // 5. Notify counterparty of the reversion (best-effort).
  if (counterpartyUserId) {
    notifyClient({
      recipientUserId: counterpartyUserId,
      eventType: "dispatch_status_changed",
      inAppType: "dispatch_status_changed",
      title: `📦 Load Update`,
      body: `Status reverted to ${STATUS_LABEL[recomputed] ?? recomputed}`,
      link: shipperLoadLink(loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, status: recomputed, undone_stage: stageKey },
    });
  }

  return recomputed;
}

export interface EditStageTimeArgs {
  dispatchId: string;
  stageKey: StageKey;
  newAtIso: string;
  actorId: string;
}

/**
 * Rewrite the timestamp of the newest matching status_* event for a stage.
 * Does NOT change direct_dispatches.status or any other stage. Writes an
 * audit trail event.
 */
export async function editStageTime({
  dispatchId, stageKey, newAtIso, actorId,
}: EditStageTimeArgs): Promise<void> {
  const status = STAGE_TO_STATUS[stageKey];
  if (!status) throw new Error("Stage does not have an editable timestamp");
  const targetType = `status_${status}`;

  const { data: rows } = await supabase
    .from("direct_dispatch_events" as any)
    .select("id, created_at")
    .eq("dispatch_id", dispatchId)
    .eq("event_type", targetType)
    .order("created_at", { ascending: false })
    .limit(1);
  const row = (rows as any[] | null)?.[0];
  if (!row) {
    // No pre-existing event (stage was auto-derived from dispatch.status /
    // load.status). Insert a new status_* event at the chosen time so the
    // tracker + timeline have a real timestamp going forward.
    const { error: insErr } = await supabase.from("direct_dispatch_events" as any).insert({
      dispatch_id: dispatchId,
      actor_id: actorId,
      event_type: targetType,
      payload: { status, backfilled: true, synthesized_by: "edit_time" },
      created_at: newAtIso,
    });
    if (insErr) throw insErr;
    await supabase.from("direct_dispatch_events" as any).insert({
      dispatch_id: dispatchId,
      actor_id: actorId,
      event_type: "status_time_edited",
      payload: { stage: stageKey, status, old_at: null, new_at: newAtIso },
    });
    return;
  }
  const oldAt = row.created_at as string;

  const { data: updatedRows, error: updErr } = await supabase
    .from("direct_dispatch_events" as any)
    .update({ created_at: newAtIso })
    .eq("id", row.id)
    .select("id");
  if (updErr) throw updErr;
  if (!updatedRows || updatedRows.length === 0) {
    throw new Error(
      "Change time failed: the event was not updated. You may not have permission to edit this stage."
    );
  }

  await supabase.from("direct_dispatch_events" as any).insert({
    dispatch_id: dispatchId,
    actor_id: actorId,
    event_type: "status_time_edited",
    payload: { stage: stageKey, status, old_at: oldAt, new_at: newAtIso },
  });
}

export { STATUS_TO_STAGE };

// ─────────────────────────────────────────────────────────────────────────────
// Dynamic per-stop tracker helpers (rebuilt tracker)
// ─────────────────────────────────────────────────────────────────────────────

import type { DerivedStage, StopSubKind } from "@/lib/lrDirect/deriveStages";
import { stopEventType } from "@/lib/lrDirect/deriveStages";

/** Convert a broker stage key into the legacy dispatch.status enum so the
 *  status badge & other consumers keep working alongside the new events. */
function coerceStopStatusForDispatch(
  stopType: "pickup" | "delivery",
  sub: StopSubKind,
): DispatchStatus {
  if (stopType === "pickup") return sub === "checked_in" ? "at_pickup" : "picked_up";
  return sub === "checked_in" ? "at_delivery" : "delivered";
}

export interface SetStopStageArgs {
  dispatchId: string;
  loadId: string;
  stopId: string;
  stopNumber: number;
  stopType: "pickup" | "delivery";
  subKind: StopSubKind;
  actorId: string;
  counterpartyUserId: string | null;
  shipperUserId?: string | null;
  loadNumber?: string | null;
}

/** Mark a stop stage done. Writes stop row + event, nudges dispatch.status. */
export async function setStopStage(args: SetStopStageArgs): Promise<void> {
  const {
    dispatchId, loadId, stopId, stopNumber, stopType, subKind, actorId,
    counterpartyUserId, shipperUserId, loadNumber,
  } = args;
  const nowIso = new Date().toISOString();

  // 1. Stop row update. Use .select("id") to detect silent RLS failures.
  const patch: Record<string, any> = {};
  if (subKind === "checked_in") {
    patch.status = "checked_in";
    patch.checked_in_at = nowIso;
  } else {
    patch.status = "done";
    patch.completed_at = nowIso;
    // If they skipped the check-in step, backfill so the earlier circle isn't left empty.
    patch.checked_in_at = nowIso;
  }
  const { data: stopRows, error: stopErr } = await supabase
    .from("direct_dispatch_stops" as any)
    .update(patch)
    .eq("id", stopId)
    .select("id");
  if (stopErr) throw stopErr;
  if (!stopRows || stopRows.length === 0) {
    throw new Error("Update failed: no stop row was changed. You may not have permission.");
  }

  // 2. Event
  const eventType = stopEventType(stopType, stopNumber, subKind);
  const { data: stopEventRows, error: stopEventErr } = await supabase
    .from("direct_dispatch_events" as any)
    .insert({
      dispatch_id: dispatchId,
      actor_id: actorId,
      event_type: eventType,
      payload: { stop_id: stopId, stop_number: stopNumber, stop_type: stopType, sub: subKind },
    })
    .select("id");
  if (stopEventErr) throw stopEventErr;
  if (!stopEventRows || stopEventRows.length === 0) {
    throw new Error("Update failed: no timeline event was created.");
  }

  // 3. Nudge dispatch.status so the plain-English badge stays right.
  const nextStatus = coerceStopStatusForDispatch(stopType, subKind);
  const { data: dispatchRows, error: dispatchErr } = await supabase
    .from("direct_dispatches" as any)
    .update({ status: nextStatus, updated_at: nowIso })
    .eq("id", dispatchId)
    .select("id");
  if (dispatchErr) throw dispatchErr;
  if (!dispatchRows || dispatchRows.length === 0) {
    throw new Error("Update failed: dispatch status was not changed. You may not have permission.");
  }

  // 4. Milestone notification to shipper (loaded / delivered only).
  if (subKind === "done" && shipperUserId && shipperUserId !== actorId) {
    const milestoneKey = stopType === "pickup" ? "picked_up" : "delivered";
    notifyClient({
      recipientUserId: shipperUserId,
      eventType: `load_milestone_${milestoneKey}`,
      inAppType: "load_milestone",
      title: "📦 Load Update",
      body: `${stopType === "pickup" ? "Picked up" : "Delivered"}${loadNumber ? ` — ${loadNumber}` : ""} (stop ${stopNumber})`,
      link: shipperLoadLink(loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, stop_id: stopId, stop_type: stopType, stop_number: stopNumber },
    });
  }
  if (counterpartyUserId && counterpartyUserId !== shipperUserId) {
    notifyClient({
      recipientUserId: counterpartyUserId,
      eventType: "dispatch_status_changed",
      inAppType: "dispatch_status_changed",
      title: "📦 Load Update",
      body: `${STATUS_LABEL[nextStatus] ?? nextStatus} (stop ${stopNumber})`,
      link: dispatchLinkFor(counterpartyUserId, shipperUserId, loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, status: nextStatus, stop_id: stopId },
    });
  }
}

export interface UndoStopStageArgs {
  dispatchId: string;
  loadId: string;
  stopId: string;
  stopNumber: number;
  stopType: "pickup" | "delivery";
  subKind: StopSubKind;
  actorId: string;
}

/** Undo a stop stage. If undoing a `checked_in`, also cascades its `done` sibling. */
export async function undoStopStage(args: UndoStopStageArgs): Promise<void> {
  const { dispatchId, stopId, stopNumber, stopType, subKind, actorId } = args;

  // Determine which events to delete + what state to revert the row to.
  const eventsToDelete: string[] = [];
  const rowPatch: Record<string, any> = {};
  if (subKind === "done") {
    eventsToDelete.push(stopEventType(stopType, stopNumber, "done"));
    rowPatch.status = "checked_in";
    rowPatch.completed_at = null;
  } else {
    // Undoing checked_in also clears the done sibling.
    eventsToDelete.push(
      stopEventType(stopType, stopNumber, "checked_in"),
      stopEventType(stopType, stopNumber, "done"),
    );
    rowPatch.status = "pending";
    rowPatch.checked_in_at = null;
    rowPatch.completed_at = null;
  }

  const { data: deleted, error: delErr } = await supabase
    .from("direct_dispatch_events" as any)
    .delete()
    .eq("dispatch_id", dispatchId)
    .in("event_type", eventsToDelete)
    .select("id");
  if (delErr) throw delErr;

  const { data: rowUpd, error: rowErr } = await supabase
    .from("direct_dispatch_stops" as any)
    .update(rowPatch)
    .eq("id", stopId)
    .select("id");
  if (rowErr) throw rowErr;
  if ((!deleted || deleted.length === 0) && (!rowUpd || rowUpd.length === 0)) {
    throw new Error("Undo failed: nothing was changed. You may not have permission.");
  }

  const { data: auditRows, error: auditErr } = await supabase
    .from("direct_dispatch_events" as any)
    .insert({
      dispatch_id: dispatchId,
      actor_id: actorId,
      event_type: "status_undone",
      payload: {
        stop_id: stopId, stop_number: stopNumber, stop_type: stopType, sub: subKind,
        cleared_event_types: eventsToDelete,
      },
    })
    .select("id");
  if (auditErr) throw auditErr;
  if (!auditRows || auditRows.length === 0) {
    throw new Error("Undo failed: the audit event was not recorded.");
  }
}

/** Set a dispatch-level broker stage: truck_en_route | in_transit. */
export async function setDispatchStage(args: {
  dispatchId: string;
  loadId: string;
  stageKey: "truck_en_route" | "in_transit";
  actorId: string;
  counterpartyUserId: string | null;
  shipperUserId?: string | null;
  loadNumber?: string | null;
}): Promise<void> {
  const { dispatchId, loadId, stageKey, actorId, counterpartyUserId, shipperUserId, loadNumber } = args;
  const nextStatus: DispatchStatus =
    stageKey === "truck_en_route" ? "en_route_to_pickup" : "en_route_to_delivery";

  const { data: updatedRows, error: updErr } = await supabase
    .from("direct_dispatches" as any)
    .update({ status: nextStatus, updated_at: new Date().toISOString() })
    .eq("id", dispatchId)
    .select("id");
  if (updErr) throw updErr;
  if (!updatedRows || updatedRows.length === 0) {
    throw new Error("Dispatch stage update failed: no dispatch row was updated. You may not have permission.");
  }

  const { data: eventRows, error: eventErr } = await supabase.from("direct_dispatch_events" as any).insert({
    dispatch_id: dispatchId,
    actor_id: actorId,
    event_type: `status_${stageKey}`,
    payload: { stage: stageKey },
  }).select("id");
  if (eventErr) throw eventErr;
  if (!eventRows || eventRows.length === 0) {
    throw new Error("Dispatch stage update failed: no timeline event was created.");
  }

  if (counterpartyUserId) {
    notifyClient({
      recipientUserId: counterpartyUserId,
      eventType: "dispatch_status_changed",
      inAppType: "dispatch_status_changed",
      title: "📦 Load Update",
      body: stageKey === "truck_en_route" ? "Truck en route" : "In transit",
      link: dispatchLinkFor(counterpartyUserId, shipperUserId, loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, status: nextStatus },
    });
  }
  if (shipperUserId && shipperUserId !== actorId && shipperUserId !== counterpartyUserId) {
    notifyClient({
      recipientUserId: shipperUserId,
      eventType: "dispatch_status_changed",
      inAppType: "dispatch_status_changed",
      title: "📦 Load Update",
      body: `${stageKey === "truck_en_route" ? "Truck en route" : "In transit"}${loadNumber ? ` — ${loadNumber}` : ""}`,
      link: shipperLoadLink(loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, status: nextStatus },
    });
  }
}

/** Undo a dispatch-level broker stage (truck_en_route | in_transit). */
export async function undoDispatchStageKey(args: {
  dispatchId: string;
  stageKey: "truck_en_route" | "in_transit";
  actorId: string;
}): Promise<void> {
  const { dispatchId, stageKey, actorId } = args;
  const legacy = stageKey === "truck_en_route" ? "status_assigned" : "status_en_route_to_delivery";
  const eventTypes = [`status_${stageKey}`, legacy];
  if (stageKey === "truck_en_route") eventTypes.push("status_en_route_to_pickup");

  const { data: deleted, error: delErr } = await supabase
    .from("direct_dispatch_events" as any)
    .delete()
    .eq("dispatch_id", dispatchId)
    .in("event_type", eventTypes)
    .select("id");
  if (delErr) throw delErr;
  if (!deleted || deleted.length === 0) {
    throw new Error("Undo failed: no events were removed. You may not have permission.");
  }

  const { data: auditRows, error: auditErr } = await supabase
    .from("direct_dispatch_events" as any)
    .insert({
      dispatch_id: dispatchId,
      actor_id: actorId,
      event_type: "status_undone",
      payload: { stage: stageKey, cleared_event_types: eventTypes },
    })
    .select("id");
  if (auditErr) throw auditErr;
  if (!auditRows || auditRows.length === 0) {
    throw new Error("Undo failed: the audit event was not recorded.");
  }
}

/** Edit the timestamp of the latest event for a given stage — works for both
 *  broker_stop stages (per-stop events) and broker_dispatch stages. */
export async function editStageTimeDynamic(args: {
  dispatchId: string;
  stage: DerivedStage;
  newAtIso: string;
  actorId: string;
}): Promise<void> {
  const { dispatchId, stage, newAtIso, actorId } = args;
  let targetType: string;
  if (stage.kind === "broker_stop" && stage.stopType && stage.stopNumber && stage.subKind) {
    targetType = stopEventType(stage.stopType, stage.stopNumber, stage.subKind);
  } else if (stage.kind === "broker_dispatch") {
    targetType = `status_${stage.key}`;
  } else {
    throw new Error("Stage does not have an editable timestamp");
  }

  const { data: rows } = await supabase
    .from("direct_dispatch_events" as any)
    .select("id, created_at")
    .eq("dispatch_id", dispatchId)
    .eq("event_type", targetType)
    .order("created_at", { ascending: false })
    .limit(1);
  const row = (rows as any[] | null)?.[0];
  if (!row) {
    const { error: insErr } = await supabase.from("direct_dispatch_events" as any).insert({
      dispatch_id: dispatchId,
      actor_id: actorId,
      event_type: targetType,
      payload: { synthesized_by: "edit_time" },
      created_at: newAtIso,
    });
    if (insErr) throw insErr;
    return;
  }
  const { data: updated, error: updErr } = await supabase
    .from("direct_dispatch_events" as any)
    .update({ created_at: newAtIso })
    .eq("id", row.id)
    .select("id");
  if (updErr) throw updErr;
  if (!updated || updated.length === 0) {
    throw new Error("Change time failed: the event was not updated. You may not have permission.");
  }

  // Also update the corresponding stop row timestamp if applicable.
  if (stage.kind === "broker_stop" && stage.stopId && stage.subKind) {
    const patch: Record<string, any> =
      stage.subKind === "done" ? { completed_at: newAtIso } : { checked_in_at: newAtIso };
    await supabase.from("direct_dispatch_stops" as any).update(patch).eq("id", stage.stopId);
  }

  await supabase.from("direct_dispatch_events" as any).insert({
    dispatch_id: dispatchId,
    actor_id: actorId,
    event_type: "status_time_edited",
    payload: { stage: stage.key, old_at: row.created_at, new_at: newAtIso },
  });
}

/** Set a dispatch ETA (pickup or delivery). Writes column + event trail. */
export async function setDispatchEta(args: {
  dispatchId: string;
  loadId: string;
  kind: "pickup" | "delivery";
  newAtIso: string | null;
  actorId: string;
  counterpartyUserId: string | null;
  shipperUserId?: string | null;
}): Promise<void> {
  const { dispatchId, loadId, kind, newAtIso, actorId, counterpartyUserId, shipperUserId } = args;
  const nowIso = new Date().toISOString();

  // Peek prior value to decide event type (set vs updated).
  const { data: prior } = await supabase
    .from("direct_dispatches" as any)
    .select(kind === "pickup" ? "pickup_eta_at" : "delivery_eta_at")
    .eq("id", dispatchId)
    .maybeSingle();
  const priorAt = (prior as any)?.[kind === "pickup" ? "pickup_eta_at" : "delivery_eta_at"] ?? null;

  const patch: Record<string, any> =
    kind === "pickup"
      ? { pickup_eta_at: newAtIso, pickup_eta_updated_at: nowIso, pickup_eta_updated_by: actorId }
      : { delivery_eta_at: newAtIso, delivery_eta_updated_at: nowIso, delivery_eta_updated_by: actorId };

  const { data: updated, error: updErr } = await supabase
    .from("direct_dispatches" as any)
    .update(patch)
    .eq("id", dispatchId)
    .select("id");
  if (updErr) throw updErr;
  if (!updated || updated.length === 0) {
    throw new Error("ETA update failed. You may not have permission.");
  }

  const evType = priorAt ? `${kind}_eta_updated` : `${kind}_eta_set`;
  await supabase.from("direct_dispatch_events" as any).insert({
    dispatch_id: dispatchId,
    actor_id: actorId,
    event_type: evType,
    payload: { kind, new_at: newAtIso, prior_at: priorAt },
  });

  // Notify shipper (broker sets ETAs → shipper cares).
  if (shipperUserId && shipperUserId !== actorId) {
    notifyClient({
      recipientUserId: shipperUserId,
      eventType: "dispatch_status_changed",
      inAppType: "dispatch_status_changed",
      title: `📦 ${kind === "pickup" ? "Pickup" : "Delivery"} ETA updated`,
      body: newAtIso
        ? new Date(newAtIso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
        : "Cleared",
      link: shipperLoadLink(loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, eta_kind: kind, eta_at: newAtIso },
    });
  }
  if (counterpartyUserId && counterpartyUserId !== shipperUserId) {
    notifyClient({
      recipientUserId: counterpartyUserId,
      eventType: "dispatch_status_changed",
      inAppType: "dispatch_status_changed",
      title: `📦 ${kind === "pickup" ? "Pickup" : "Delivery"} ETA updated`,
      body: newAtIso ? new Date(newAtIso).toLocaleString() : "Cleared",
      link: dispatchLinkFor(counterpartyUserId, shipperUserId, loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, eta_kind: kind, eta_at: newAtIso },
    });
  }
}

/** Set / update / clear the external tracking URL on a dispatch. */
export async function setDispatchTrackingUrl(args: {
  dispatchId: string;
  loadId: string;
  newUrl: string | null;
  actorId: string;
  counterpartyUserId: string | null;
  shipperUserId?: string | null;
}): Promise<void> {
  const { dispatchId, loadId, newUrl, actorId, counterpartyUserId, shipperUserId } = args;
  const nowIso = new Date().toISOString();

  const cleaned = newUrl && newUrl.trim() ? newUrl.trim() : null;
  if (cleaned && !/^https?:\/\/[^\s]+$/i.test(cleaned)) {
    throw new Error("Tracking link must start with http:// or https://");
  }

  const { data: prior } = await supabase
    .from("direct_dispatches" as any)
    .select("tracking_url")
    .eq("id", dispatchId)
    .maybeSingle();
  const priorUrl = (prior as any)?.tracking_url ?? null;

  const { data: updated, error: updErr } = await supabase
    .from("direct_dispatches" as any)
    .update({
      tracking_url: cleaned,
      tracking_url_updated_at: nowIso,
      tracking_url_updated_by: actorId,
    })
    .eq("id", dispatchId)
    .select("id");
  if (updErr) throw updErr;
  if (!updated || updated.length === 0) {
    throw new Error("Tracking link update failed. You may not have permission.");
  }

  const evType = cleaned == null ? "tracking_link_cleared" : priorUrl ? "tracking_link_updated" : "tracking_link_set";
  await supabase.from("direct_dispatch_events" as any).insert({
    dispatch_id: dispatchId,
    actor_id: actorId,
    event_type: evType,
    payload: { new_url: cleaned, prior_url: priorUrl },
  });

  const seen = new Set<string>();
  for (const uid of [shipperUserId, counterpartyUserId]) {
    if (!uid || uid === actorId || seen.has(uid)) continue;
    seen.add(uid);
    notifyClient({
      recipientUserId: uid,
      eventType: "dispatch_status_changed",
      inAppType: "dispatch_status_changed",
      title: cleaned ? "🔗 Tracking link available" : "Tracking link removed",
      body: cleaned ? "Broker shared an external tracking link" : "",
      link: dispatchLinkFor(uid, shipperUserId, loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, tracking_url: cleaned },
    });
  }
}





